import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {httpGetCountries, HttpGetCountriesOptions} from './address.http'
import {z} from 'zod'
import {Country, PaginationResponse} from '@/types'

export const HttpGetCountriesResponse = z.object({
    data: Country.array(),
    meta: PaginationResponse
})
export type HttpGetCountriesResponse = z.infer<typeof HttpGetCountriesResponse>

export const useGetCountries = (
    promiseArgs: Omit<HttpGetCountriesOptions, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetCountriesResponse>, 'enabled' | 'staleTime'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.COUNTRIES, searchValue, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetCountries({
                    page: pageParam,
                    search: searchValue || promiseArgs.search,
                    limit: promiseArgs.limit
                }),
                responseShape: HttpGetCountriesResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
